import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { SIGNUP_URL, engagementHero } from '../engagementContent';
import { AUDIENCE_TOGGLE_SPACER_CLASS } from './FloatingAudienceToggle';
import VideoDemoModal from './VideoDemoModal';

const HeroSection = ({
  eyebrow = 'Hire your next team member',
  headline = (
    <>
      Your AI
      <br className="hidden sm:block" />
      <span className="sm:ml-0"> Customer Engagement Employee</span>
    </>
  ),
  subhead = 'Kolanut handles personalized outreach, churn prevention, and loyalty campaigns around the clock. Never sick days. Never off the clock.',
  hero = engagementHero,
  ctaLabel = 'Get Started',
  signupUrl = SIGNUP_URL,
  demoVideoSrc = '/kolanut.mp4',
  demoLabel = 'Watch Demo',
  showAudienceToggleSpacer = true,
}) => {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <section className="bg-white pt-2 sm:pt-3 pb-4 sm:pb-5">
      <div className="px-2 sm:px-3 md:px-4">
        {showAudienceToggleSpacer && (
          <div className={AUDIENCE_TOGGLE_SPACER_CLASS} aria-hidden />
        )}

        <div className="relative isolate overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] lg:rounded-[2.5rem] min-h-[min(86vh,42rem)] sm:min-h-[min(82vh,40rem)] lg:min-h-[min(78vh,44rem)] xl:min-h-[46rem]">
          <img
            src={hero.imageSrc}
            alt={hero.imageAlt}
            className={`absolute inset-0 h-full w-full object-cover saturate-[0.9] contrast-[0.96] ${hero.imagePosition ?? 'object-[center_35%] sm:object-center'}`}
          />
          <div
            className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/15 sm:from-black/70 sm:via-black/25 sm:to-black/10"
            aria-hidden
          />
          <div
            className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent sm:from-black/45"
            aria-hidden
          />

          <div className="relative z-10 flex min-h-[inherit] flex-col justify-end px-5 pt-5 pb-28 sm:px-10 sm:pt-6 sm:pb-32 lg:px-14 lg:pt-8 lg:pb-36">
            <div className="mx-auto w-full max-w-xl text-center sm:mx-0 sm:max-w-2xl sm:text-left">
              <p className="inline-block rounded-full border border-white/35 bg-white/15 px-3.5 py-1.5 text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] text-white/95 backdrop-blur-sm">
                {eyebrow}
              </p>

              <h1 className="mt-5 font-serif text-[2.35rem] leading-[1.1] text-white sm:mt-6 sm:text-5xl md:text-[3.25rem] lg:text-6xl lg:leading-[1.08]">
                {headline}
              </h1>

              <p className="mt-4 max-w-lg text-lg leading-relaxed text-white/85 sm:mt-5 sm:text-xl sm:text-white/90 mx-auto sm:mx-0">
                {subhead}
              </p>

              <div className="mt-7 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                <a
                  href={signupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 md:h-14 px-8 md:px-10 rounded-full bg-[#8ae690] text-black font-semibold text-sm md:text-base shadow-md hover:bg-[#7ad67f] hover:shadow-lg transition-all duration-200"
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/70">
                    <img src="/kolanut_logo.png" alt="" className="w-4 h-4 object-contain" />
                  </span>
                  {ctaLabel}
                </a>

                <button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2.5 h-12 md:h-14 px-8 md:px-10 rounded-full border border-white/40 bg-white/10 text-white font-semibold text-sm md:text-base backdrop-blur-sm hover:bg-white/20 transition-all duration-200"
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/20">
                    <Play className="w-3.5 h-3.5 fill-current" />
                  </span>
                  {demoLabel}
                </button>
              </div>
            </div>
          </div>

          <span className="sr-only">{hero.imageHint}</span>
        </div>
      </div>

      <VideoDemoModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        videoSrc={demoVideoSrc}
        title="Watch Kolanut in action"
      />
    </section>
  );
};

export default HeroSection;
